import {GetProductBulkResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {lazy} from '@aboutyou/lazy-imports';
import {
  BadgesContainer,
  COUPON_SLIDER_HEIGHT_CSS_VAR_NAME,
  FixedRatioContainer,
  FullScreenOverlay,
  FullScreenOverlayChildrenProps,
  HorizontalSimpleSlider,
  LoadingPageSpinner,
  ProductImage,
  StyledSliderBullets,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {
  ComponentProps,
  ComponentType,
  memo,
  useEffect,
  useRef,
} from 'react';
import {BadgeList} from '../../../features/BadgeList/BadgeList';
import {useBadgesList} from '../../../features/BadgeList/useBadgesList';
import {useAddToBasketRoundCtaAction} from '../AddToBasketSection/hooks/useAddToBasketRoundCtaAction';
import {GoBackWithCategoryLabel} from '../GoBackWithCategoryLabel/GoBackWithCategoryLabel';
import {useSizesSection} from '../useGetProductBulkSection';
import {PRODUCT_ZOOM_KEY} from '../utils';
import {useGetBadgeListKeys} from '../utils/useGetBadgeListKeys';
import {CTAs} from './CTAs/CTAs';
import {SliderContent} from './SliderContent';

const ProductImageZoom = lazy(
  () =>
    import(
      '@aboutyou/ui-components/src/components/molecules/ProductImageZoom/ProductImageZoom'
    ).then(mod => ({default: mod.ProductImageZoom})),
  {
    fallback: <LoadingPageSpinner />,
  },
);

export interface ProductSliderProps {
  scrollToShopTheLook?: VoidFunction;
  isDefaultState?: boolean;
  isAddToBasketInView: boolean;
  animateElements?: boolean;
  gridColumns?: number;
  /**
   * When true, suppresses the over-gallery back button, BadgeList, and the
   * over-gallery Add to Basket button entirely. Used by quick view v2 which
   * surfaces those affordances elsewhere — hiding via CSS caused a flicker on
   * first paint, this avoids it by not rendering them at all.
   */
  hideOverGalleryControls?: boolean;
}

const MemoGoBackWithCategoryLabel = memo(GoBackWithCategoryLabel);

export const ProductSliderView: React.FC<
  GetProductBulkResponse['imagesSection'] &
    ProductSliderProps & {
      isDefaultState?: boolean;
    }
> = ({
  product,
  brand,
  badges,
  scrollToShopTheLook,
  isDefaultState,
  isAddToBasketInView,
  animateElements = false,
  gallery,
  gridColumns,
  hideOverGalleryControls = false,
}) => {
  const currentSlide = useRef(0);
  const {data: sizeSection} = useSizesSection();

  useEffect(() => {
    currentSlide.current = 0;
  }, [gallery.elements]);

  const onIndexChange = (slide: number) => {
    currentSlide.current = slide;
  };

  const badgesList = useBadgesList({
    badges,
    campaignSale: null,
    sale: null,
    showPriceOverhaulV2SaleLabel: false,
  });
  const {badgesListKeys} = useGetBadgeListKeys({
    isInMiniADPDialog: false,
  });

  const {
    handleClick: handleRoundCtaClick,
    isHidden: isRoundCtaHidden,
    isLoading: isRoundCtaLoading,
  } = useAddToBasketRoundCtaAction({sizesSection: sizeSection});

  if (product?.isSoldOut) {
    return null;
  }

  const bottomSpaceOnBadgeList = badges.some(
    badge =>
      badge.type?.$case === 'campaignSale' &&
      badge.type.campaignSale.countdown?.endsAt,
  );

  return (
    <StyledFixedRatioContainer
      $singleImgProduct={gallery.elements.length === 1}
      ratio="3:4"
      dataTestId="ProductSliderWrapper"
    >
      {!hideOverGalleryControls && <MemoGoBackWithCategoryLabel />}
      <ImageFullScreenOverlay
        openStateKey={PRODUCT_ZOOM_KEY}
        isOpenByDefault={false}
        dataTestId="ProductSliderFullscreen"
        renderInPortal={false}
      >
        {({open, isOpen}: FullScreenOverlayChildrenProps) => {
          return (
            <StyledHorizontalSimpleSlider
              showNavButtons={isOpen}
              navOverSlider={isOpen}
              onClick={() => {
                open();
              }}
              dataTestId="productSlider"
              onIndexChange={onIndexChange}
              initialSlide={currentSlide.current}
              uniqueKey={`product_img_slider_${product.id}`}
              defaultSlidesNumber={gallery.elements.length}
            >
              <SliderContent
                isOpen={Boolean(isOpen)}
                scrollToShopTheLook={scrollToShopTheLook}
                imageComponent={
                  (isOpen ? ProductImageZoom : ProductImage) as ComponentType<
                    ComponentProps<typeof ProductImage>
                  >
                }
                activeIndex={currentSlide.current}
                isLoadingState={isDefaultState}
                columns={gridColumns ?? 1}
                gallery={gallery}
              />
            </StyledHorizontalSimpleSlider>
          );
        }}
      </ImageFullScreenOverlay>
      {!hideOverGalleryControls && (
        <FloatingElementsWrapper>
          {badges && (
            <StyledBadgeList
              badgesList={badgesList}
              bottomSpace={bottomSpaceOnBadgeList}
              $fadeIn={animateElements}
            />
          )}
        </FloatingElementsWrapper>
      )}
      <StyledCTAs
        product={product}
        price={sizeSection?.price}
        brand={brand}
        onAddToBasketIconClick={handleRoundCtaClick}
        isAddToBasketInView={isAddToBasketInView}
        isAddToBasketLoading={isRoundCtaLoading}
        hideAddToBasketButton={hideOverGalleryControls || isRoundCtaHidden}
        $fadeIn={animateElements}
        badgesListKeys={badgesListKeys}
      />
    </StyledFixedRatioContainer>
  );
};

const StyledFixedRatioContainer = styled(FixedRatioContainer)<{
  $singleImgProduct: boolean;
}>`
  // this prevents the ripples on CTAs to cause the component to overflow horizontally
  overflow: hidden;

  ${BadgesContainer} {
    z-index: ${Z_INDEXES.PRODUCT_SLIDER_BADGES_CONTAINER};
  }
`;

const ImageFullScreenOverlay = styled(FullScreenOverlay)`
  display: flex;
  flex-direction: column;
`;

const StyledCTAs = styled(CTAs)<{$fadeIn: boolean}>`
  bottom: var(${COUPON_SLIDER_HEIGHT_CSS_VAR_NAME}, 0px);
  animation: fadein ease-in-out ${({$fadeIn}) => ($fadeIn ? '0.3s' : '0s')};

  @keyframes fadein {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
`;

const StyledBadgeList = styled(BadgeList)<{$fadeIn: boolean}>`
  animation: fadein ease-in-out ${({$fadeIn}) => ($fadeIn ? '0.3s' : '0s')};

  @keyframes fadein {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }
`;

const StyledHorizontalSimpleSlider = styled(HorizontalSimpleSlider)`
  &&& ${StyledSliderBullets} {
    bottom: var(${COUPON_SLIDER_HEIGHT_CSS_VAR_NAME}, 0px);
  }
`;

const FloatingElementsWrapper = styled.div`
  position: absolute;
  bottom: var(${COUPON_SLIDER_HEIGHT_CSS_VAR_NAME}, 0px);
  left: 0;
  width: 100%;
`;
